import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { GlobalConfigProvider } from './contexts/GlobalConfigContext';
import { EntitlementsProvider } from './contexts/EntitlementsContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import Layout from './components/Layout';
import AnimatedBackdrop from './components/ui/AnimatedBackdrop';
import Login from './pages/Login';
import SetPassword from './pages/SetPassword';
import Signup from './pages/Signup';
import SignupStatus from './pages/SignupStatus';
import LegalDocument from './pages/LegalDocument';
import SupportRedirect from './pages/SupportRedirect';
import Dashboard from './pages/Dashboard';
import FinancialDashboard from './pages/FinancialDashboard';
import Birds from './pages/Birds';
import Species from './pages/Species';
import Breeding from './pages/Breeding';
import Clutches from './pages/Clutches';
import Chicks from './pages/Chicks';
import HealthManagement, { HealthTestsRedirect } from './pages/HealthManagement';
import Documents from './pages/Documents';
import Photos from './pages/Photos';
import Notes from './pages/Notes';
import NutritionManagement, { FeedStockRedirect, NutritionalTrackingRedirect } from './pages/NutritionManagement';
import Accounting from './pages/Accounting';
import Reports from './pages/Reports';
import Notifications from './pages/Notifications';
import Users from './pages/Users';
import Profile from './pages/Profile';
import Portal from './pages/Portal';
import Admin from './pages/Admin';
import SystemSettings from './pages/SystemSettings';
// Financial Management
import Sales from './pages/Sales';
import Purchases from './pages/Purchases';
import Expenses from './pages/Expenses';
import Payments from './pages/Payments';
import Transactions from './pages/Transactions';
// Inventory & Operations
import Supplies from './pages/Supplies';
import Suppliers from './pages/Suppliers';
import Customers from './pages/Customers';
import Equipment from './pages/Equipment';
import Cages from './pages/Cages';
import Tasks from './pages/Tasks';
import BirdChatter from './pages/Copilot';
import KnowledgeLibrary from './pages/KnowledgeLibrary';
// Marketplace
import Marketplace from './pages/Marketplace';
import CreateListingForm from './components/marketplace/CreateListingForm';
import WhatsAppGroupsManager from './components/marketplace/WhatsAppGroupsManager';
import MessageTemplatesManager from './components/marketplace/MessageTemplatesManager';
import AdvancedAnalytics from './components/marketplace/AdvancedAnalytics';
import OrdersManager from './components/marketplace/OrdersManager';
import NewOrderForm from './components/marketplace/NewOrderForm';

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Login />}
      />
      <Route
        path="/set-password"
        element={isAuthenticated ? <Navigate to="/" replace /> : <SetPassword />}
      />
      <Route
        path="/signup"
        element={isAuthenticated ? <Navigate to="/" replace /> : <Signup />}
      />
      <Route path="/signup/status/:id" element={<SignupStatus />} />
      <Route path="/legal/:slug" element={<LegalDocument />} />
      {/* Public support deep-link; redirects to the AI Support Platform. */}
      <Route path="/support" element={<SupportRedirect />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="financial-dashboard" element={<FinancialDashboard />} />
        <Route path="species" element={<Species />} />
        <Route path="birds" element={<Birds />} />
        <Route path="cages" element={<Cages />} />
        <Route path="breeding" element={<Breeding />} />
        <Route path="clutches" element={<Clutches />} />
        <Route path="chicks" element={<Chicks />} />
        <Route path="health-tests" element={<HealthTestsRedirect />} />
        <Route path="health-management" element={<HealthManagement />} />
        <Route path="documents" element={<Documents />} />
        <Route path="photos" element={<Photos />} />
        <Route path="notes" element={<Notes />} />
        <Route path="nutrition-management" element={<NutritionManagement />} />
        <Route path="feed-stock" element={<FeedStockRedirect />} />
        <Route path="nutritional-tracking" element={<NutritionalTrackingRedirect />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="birdchatter" element={<BirdChatter />} />
        <Route path="knowledge" element={<KnowledgeLibrary />} />
        <Route path="users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="profile" element={<Profile />} />
        <Route path="portal" element={<Portal />} />
        <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="system-settings" element={<SystemSettings />} />
        {/* Financial Management Routes */}
        <Route path="sales" element={<Sales />} />
        <Route path="purchases" element={<Purchases />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="payments" element={<Payments />} />
        <Route path="transactions" element={<Transactions />} />
        {/* Inventory & Operations Routes */}
        <Route path="supplies" element={<Supplies />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="customers" element={<Customers />} />
        <Route path="equipment" element={<Equipment />} />
        {/* Marketplace Routes */}
        <Route path="marketplace" element={<Marketplace />} />
        <Route path="marketplace/listings" element={<Marketplace />} />
        <Route path="marketplace/listings/new" element={<CreateListingForm />} />
        <Route path="marketplace/orders" element={<Marketplace />} />
        <Route path="marketplace/orders/new" element={<NewOrderForm />} />
        <Route path="marketplace/whatsapp" element={<Marketplace />} />
        <Route path="marketplace/templates" element={<Marketplace />} />
        <Route path="marketplace/analytics" element={<Marketplace />} />
        <Route path="marketplace/automation" element={<Marketplace />} />
        <Route path="marketplace/integration" element={<Marketplace />} />
        <Route path="marketplace/settings" element={<Marketplace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <EntitlementsProvider>
        <GlobalConfigProvider>
          <div className="relative min-h-screen overflow-x-hidden">
            <AnimatedBackdrop className="fixed inset-0" />
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                style: {
                  borderRadius: '14px',
                  border: '1px solid rgba(148, 163, 184, 0.25)',
                  background: 'rgba(255,255,255,0.95)',
                  color: '#0f172a',
                  backdropFilter: 'blur(12px)',
                },
              }}
            />
          </div>
        </GlobalConfigProvider>
        </EntitlementsProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App; 